/** Owned, local product-lighting templates. The rig IDs resolve to physically
 * reflected softbox cards in captured module 85211; no overlay or remote HDR. */
const shared = {
  'scene.envId': '',
  'scene.bgMode': 'color',
  'scene.bgColor': '#030509',
  'scene.darkMode': true,
  'scene.transparentBg': false,
  'scene.contactShadow': false,
  'scene.glossyFloor': false,
  'scene.hdrYaw': 0,
  'scene.hdrPitch': 0,
  'scene.lightBrightness': 100,
  'scene.screenPower': 0.035,
  'devices.reflection': 0.08,
  'camera.fov': 24,
  'camera.panX': 0,
  'camera.panY': 0,
  'camera.flap': 0,
  'camera.flapX': 0,
  'blur.strength': 0,
  'effects.ghost': false,
  'effects.lighting': false,
  'effects.screenGlass': false,
  'effects.vignette': 0.16,
  'effects.grain': 0,
  'effects.caStrength': 0,
  'effects.bloom': true,
  'effects.bloomStrength': 0.38,
  'effects.bloomThreshold': 0.82,
  'effects.bloomRadius': 0.5,
  'post.exposure': 0,
  'post.brightness': 0,
  'post.shadows': -0.12,
  'post.midtones': 0,
  'post.highlights': 0.08,
  'post.saturation': 0.9,
};

const templates = [
  {
    id: 'iphone-pearl-rim',
    label: 'iPhone · Pearl Rim',
    modelId: 'iphone18Pro',
    kind: 'iphone',
    description: 'A dark front portrait with a razor-thin rose-white edge, luminous corner accents and a quiet display.',
    patch: {...shared,
      'scene.lightRig': 'rim-iphone-pearl',
      'camera.tiltX': -9,
      'camera.tiltY': -18,
      'camera.roll': -5,
      'camera.zoom': 3.2,
      'devices.statusBar': false,
      'devices.notch': true,
    },
  },
  {
    id: 'iphone-rose-silhouette',
    label: 'iPhone · Rose Silhouette',
    modelId: 'iphone18Pro', kind: 'iphone',
    description: 'A blacked-out rear portrait with rose-gold bevels, bright camera rings and a luminous lower edge.',
    patch: {...shared,
      'scene.lightRig': 'rim-iphone-studio', 'post.saturation': 1.25, 'post.exposure': -0.2,
      'camera.tiltX': -4, 'camera.tiltY': 165,
      'camera.roll': 0, 'camera.zoom': 3.2,
      'devices.statusBar': false, 'devices.notch': true,
    },
  },
  {
    id: 'iphone-anodized-macro',
    label: 'iPhone · Anodized Macro',
    modelId: 'iphone18Pro', kind: 'iphone',
    description: 'A close study of the camera enclosure: rose anodized grain, sculpted bevels and small glass reflections against black.',
    patch: {...shared,
      'scene.lightRig': 'rim-iphone-studio', 'post.saturation': 1.25, 'post.exposure': -0.2,
      'camera.tiltX': -10, 'camera.tiltY': 174,
      'camera.roll': 0, 'camera.zoom': 1.65,
      'camera.panY': -0.30,
      'devices.statusBar': false, 'devices.notch': true,
    },
  },
  {
    id: 'macbook-graphite-rim',
    label: 'MacBook · Graphite Rim',
    modelId: 'macbookPro14',
    kind: 'macbook',
    description: 'A deep graphite stage with cool-white bevel accents and a fine silver trace around the lid and lower enclosure.',
    patch: {...shared,
      'scene.lightRig': 'rim-macbook-graphite',
      'scene.screenPower': 0.035,
  'devices.reflection': 0.086,
      'scene.hdrYaw': 120,
      'scene.lightBrightness': 75,
      'camera.tiltX': 15,
      'camera.tiltY': -28,
      'camera.roll': -3,
      'camera.zoom': 5.5,
      'camera.panY': 0.35,
      'devices.laptopHingeAngle': 105,
      'devices.macMenuBar': false,
      'devices.macBrowser': false,
      'devices.macDock': false,
    },
  },
];

export function listRimTemplates() {
  return templates.map(({id, label, modelId, description, kind}) => ({id, label, modelId, description, kind}));
}

export function getRimTemplate(id) {
  const template = templates.find(template => template.id === id);
  if (!template) throw new Error(`Unknown lighting template: ${String(id)}`);
  return {...template, patch: {...template.patch}};
}

const nextTurn = () => new Promise(resolve => setTimeout(resolve, 32));

/** Keep the user's media and timeline. Model prep and native committed writes
 * participate in history, scene edits, recovery and image/video rendering. */
export async function applyRimTemplate(bridge, id, {settle = nextTurn} = {}) {
  const template = getRimTemplate(id);
  if (!bridge?.ready) throw new Error('Studio viewport is not ready');
  await bridge.pauseTimeline?.();
  await bridge.selectModel(template.modelId);
  // Background controls depend on mode; let React rebuild before writing color.
  bridge.commit({'scene.envId': '', 'scene.bgMode': 'color'});
  await settle();
  const patch = {...template.patch};
  if (typeof bridge.getControls === 'function') {
    const controls = bridge.getControls();
    const collect = (items, found = new Set()) => {
      for (const item of items) {
        if (item.path) found.add(item.path);
        collect(item.children ?? [], found);
        for (const tab of item.tabs ?? []) collect(tab.children ?? [], found);
      }
      return found;
    };
    const known = collect(Array.isArray(controls) ? controls : controls.controls ?? []);
    for (const path of Object.keys(patch)) if (!known.has(path)) delete patch[path];
    for (const required of ['scene.lightRig', 'scene.bgColor', 'camera.tiltX', 'camera.tiltY', 'camera.zoom']) {
      if (!(required in patch)) throw new Error(`Template control is not ready: ${required}`);
    }
  }
  bridge.commit(patch);
  let preview;
  if (bridge.pauseTimeline) {
    const deadline=Date.now()+20_000;
    do {
      preview=await bridge.pauseTimeline();
      if (!preview || preview.displayedModel===template.modelId) break;
      await settle();
    } while (Date.now()<deadline);
    if (preview && preview.displayedModel!==template.modelId) throw new Error('The model has not finished loading; try applying the template again');
  }
  const state = bridge.getState ? bridge.getState() : {modelId:template.modelId};
  return {template: template.id, displayedModel:preview?.displayedModel, ...state};
}
